import {
  useOrganisation,
  useOrganisationCarriers,
  useOrganisationRoutes,
  useReplaceOrganisationCarriers,
  useReplaceOrganisationRoutes,
  useSetOrganisationCrossingPreference,
  useSetOrganisationRoutePreferred,
} from '@my/directory-api/hooks';
import type {
  CrossingPreferenceValue,
  OrganisationCarrier,
  OrganisationRoute,
} from '@my/directory-api';
import type { FerryRouteCategory } from '@my/ferry-api';
import { ErrorBanner, FormSection, Loading, showToast } from '@my/ui';
import { Check, Edit, Star, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrossingCarrier, CrossingRoute } from '../../ferry/catalogue';
import { useCrossingCatalogue } from '../../ferry/useCrossingCatalogue';

export interface CarriersAndRoutesSectionProps {
  /** Owning organisation id; the cards stay disabled while this is null. */
  organisationId: string | null;
}

/**
 * Carriers & Routes block — sibling to `EquipmentDefinitionsSection`, same
 * inline-edit pattern. Two cards side-by-side on desktop:
 *
 *  - **Carriers** — flat picker of the carriers My.Ferry lists. Adding a
 *    carrier here auto-creates (or links) a Supplier party on the BE, so the
 *    operator can charge / receive invoices from it without a separate
 *    Directory trip.
 *  - **Routes** — My.Ferry's crossings grouped by carrier, narrowed by sea
 *    area, one chip per direction: Dublin → Holyhead and Holyhead → Dublin
 *    are separate picks, each with its own star.
 *
 * Picks saved under a catalogue-file key (from before the move to Ferry)
 * still show, and appear in edit mode in their own group so they can be
 * dropped; nothing new is picked from the file. A Ferry carrier or route the
 * file does not link yet is shown greyed: Directory would refuse the key
 * until the file has it.
 *
 * Routes are picked **independently** of carriers (per the product
 * decision): an org can opt into a route without opting into the operating
 * carrier — useful when the carrier is selected on demand at booking
 * time, or when the operator only uses agents.
 *
 * Only one block can be in edit mode at a time; the other block's Edit
 * icon disables to avoid two drafts in flight.
 */
export function CarriersAndRoutesSection({ organisationId }: CarriersAndRoutesSectionProps) {
  const { t } = useTranslation('configuration');

  const catalogue = useCrossingCatalogue();
  const carriersQuery = useOrganisationCarriers(organisationId);
  const routesQuery = useOrganisationRoutes(organisationId);
  const replaceCarriersMutation = useReplaceOrganisationCarriers();
  const replaceRoutesMutation = useReplaceOrganisationRoutes();

  type EditTarget = 'carriers' | 'routes' | null;
  const [editing, setEditing] = useState<EditTarget>(null);
  const [draft, setDraft] = useState<Set<string>>(() => new Set());
  // The keys as they were when editing began — the legacy group is built from
  // these, so a chip unticked by mistake can be ticked again before Save.
  const [editStart, setEditStart] = useState<string[]>([]);

  // O(1) lookups for chip rendering, both key styles. A saved key that
  // resolves to nothing falls back to the raw key so the operator can still
  // see it and remove it.
  const { carrierByKey, routeByKey } = catalogue;

  // The file's routes grouped by carrier — what the picker renders.
  const ferryRoutesByCarrier = useMemo(() => {
    const groups = new Map<string, { carrier: CrossingCarrier | null; routes: CrossingRoute[] }>();
    for (const route of catalogue.pickableRoutes) {
      const existing = groups.get(route.carrierKey);
      if (existing) {
        existing.routes.push(route);
      } else {
        groups.set(route.carrierKey, {
          carrier: carrierByKey.get(route.carrierKey) ?? null,
          routes: [route],
        });
      }
    }
    return Array.from(groups.entries()).map(([carrierKey, value]) => ({
      carrierKey,
      ...value,
    }));
  }, [catalogue.pickableRoutes, carrierByKey]);

  // Picks the file no longer offers under that key: a linked carrier's old
  // file key from before the move, or a key nothing resolves any more.
  const legacyCarrierPicks = useMemo(
    () => legacyPicks(editStart, catalogue.pickableCarriers, (key) => carrierByKey.get(key)?.name),
    [editStart, catalogue.pickableCarriers, carrierByKey],
  );
  const legacyRoutePicks = useMemo(
    () => legacyPicks(editStart, catalogue.pickableRoutes, (key) => routeLabel(routeByKey.get(key))),
    [editStart, catalogue.pickableRoutes, routeByKey],
  );

  const errorMessage = catalogue.fileError
    ? t('carriersAndRoutes.loadCatalogueFailed', { message: catalogue.fileError.message })
    : carriersQuery.isError
      ? t('carriersAndRoutes.loadSelectionFailed', { message: carriersQuery.error.message })
      : routesQuery.isError
        ? t('carriersAndRoutes.loadSelectionFailed', { message: routesQuery.error.message })
        : null;

  const handleEdit = (target: 'carriers' | 'routes', currentKeys: string[]) => {
    setEditing(target);
    setEditStart(currentKeys);
    setDraft(new Set(currentKeys));
  };

  const handleToggleDraft = (key: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCancel = () => {
    setEditing(null);
    setEditStart([]);
    setDraft(new Set());
  };

  const handleSaveCarriers = async () => {
    if (!organisationId) return;
    try {
      await replaceCarriersMutation.mutateAsync({
        organisationId,
        catalogueKeys: Array.from(draft),
      });
      showToast({ type: 'success', message: t('carriersAndRoutes.carriersUpdated') });
      handleCancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('carriersAndRoutes.saveFailed'),
      });
    }
  };

  const handleSaveRoutes = async () => {
    if (!organisationId) return;
    try {
      await replaceRoutesMutation.mutateAsync({
        organisationId,
        catalogueKeys: Array.from(draft),
      });
      showToast({ type: 'success', message: t('carriersAndRoutes.routesUpdated') });
      handleCancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('carriersAndRoutes.saveFailed'),
      });
    }
  };

  const isBusy = replaceCarriersMutation.isPending || replaceRoutesMutation.isPending;
  const isLoading = catalogue.isPending || carriersQuery.isPending || routesQuery.isPending;

  return (
    <>
      {errorMessage && (
        <ErrorBanner title={t('carriersAndRoutes.errorTitle')} message={errorMessage} />
      )}

      <FormSection
        title={t('carriersAndRoutes.title')}
        columns={1}
        actions={<CrossingPreferenceControl organisationId={organisationId} />}
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* ── Carriers ───────────────────────────────────────────────── */}
          <SelectionCard
            title={t('carriersAndRoutes.carriers')}
            count={carriersQuery.data?.length ?? 0}
            organisationId={organisationId}
            isLoading={isLoading}
            isBusy={isBusy}
            isEditing={editing === 'carriers'}
            editDisabledByOther={editing !== null && editing !== 'carriers'}
            onEdit={() =>
              handleEdit(
                'carriers',
                (carriersQuery.data ?? []).map((c) => c.catalogueKey),
              )
            }
            onCancel={handleCancel}
            onSave={() => void handleSaveCarriers()}
            viewBody={
              <ChipList
                selection={carriersQuery.data ?? []}
                getKey={(r) => r.catalogueKey}
                getLabel={(r) => carrierByKey.get(r.catalogueKey)?.name ?? r.catalogueKey}
                emptyLabel={t('carriersAndRoutes.emptyCarriers')}
                tone="sky"
              />
            }
            editBody={
              <CarriersEditBody
                carriers={catalogue.pickableCarriers}
                legacy={editing === 'carriers' ? legacyCarrierPicks : []}
                draft={editing === 'carriers' ? draft : new Set()}
                isBusy={isBusy}
                onToggleDraft={handleToggleDraft}
              />
            }
          />

          {/* ── Routes ─────────────────────────────────────────────────── */}
          <SelectionCard
            title={t('carriersAndRoutes.routes')}
            count={routesQuery.data?.length ?? 0}
            organisationId={organisationId}
            isLoading={isLoading}
            isBusy={isBusy}
            isEditing={editing === 'routes'}
            editDisabledByOther={editing !== null && editing !== 'routes'}
            onEdit={() =>
              handleEdit(
                'routes',
                (routesQuery.data ?? []).map((r) => r.catalogueKey),
              )
            }
            onCancel={handleCancel}
            onSave={() => void handleSaveRoutes()}
            viewBody={
              <RouteChipList
                organisationId={organisationId}
                selection={routesQuery.data ?? []}
                routeByKey={routeByKey}
                carrierByKey={carrierByKey}
                emptyLabel={t('carriersAndRoutes.emptyRoutes')}
              />
            }
            editBody={
              <RoutesEditBody
                groups={ferryRoutesByCarrier}
                legacy={editing === 'routes' ? legacyRoutePicks : []}
                draft={editing === 'routes' ? draft : new Set()}
                isBusy={isBusy}
                onToggleDraft={handleToggleDraft}
              />
            }
          />
        </div>
      </FormSection>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Shared card shell
// ────────────────────────────────────────────────────────────────────────────

interface SelectionCardProps {
  title: string;
  count: number;
  organisationId: string | null;
  isLoading: boolean;
  isBusy: boolean;
  isEditing: boolean;
  editDisabledByOther: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  viewBody: React.ReactNode;
  editBody: React.ReactNode;
}

function SelectionCard({
  title,
  count,
  organisationId,
  isLoading,
  isBusy,
  isEditing,
  editDisabledByOther,
  onEdit,
  onCancel,
  onSave,
  viewBody,
  editBody,
}: SelectionCardProps) {
  const { t } = useTranslation('configuration');
  return (
    <div className="rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          {title}
          <span className="text-xs font-normal text-slate-400">({count})</span>
        </h3>

        {!isEditing ? (
          <button
            type="button"
            aria-label={t('carriersAndRoutes.ariaEdit', { name: title })}
            title={t('carriersAndRoutes.ariaEdit', { name: title })}
            onClick={onEdit}
            disabled={!organisationId || isLoading || isBusy || editDisabledByOther}
            className="flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            <Edit className="h-3 w-3" />
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('carriersAndRoutes.ariaCancel')}
              title={t('carriersAndRoutes.ariaCancel')}
              onClick={onCancel}
              disabled={isBusy}
              className="flex h-5 w-5 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>
            <button
              type="button"
              aria-label={t('carriersAndRoutes.ariaSave')}
              title={t('carriersAndRoutes.ariaSave')}
              onClick={onSave}
              disabled={isBusy}
              className="flex h-5 w-5 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              <Check className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      <div className="px-3 py-3">
        {isLoading ? <Loading label={t('carriersAndRoutes.loading')} /> : isEditing ? editBody : viewBody}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// View mode — chip list
// ────────────────────────────────────────────────────────────────────────────

interface ChipListProps<T extends OrganisationCarrier | OrganisationRoute> {
  selection: T[];
  getKey: (row: T) => string;
  getLabel: (row: T) => string;
  emptyLabel: string;
  tone: 'sky' | 'emerald';
}

/**
 * The org's routes, each with its carrier's code — two carriers on one route
 * ("Dublin Port → Holyhead") were indistinguishable before — and a star.
 *
 * <p>
 * Starring is one click and saves at once, outside the edit mode the card's
 * pencil opens: edit mode owns WHICH routes, the star only ranks them. Sorted
 * starred first, then by route, then carrier, so a route's carriers sit
 * together.
 * </p>
 */
function RouteChipList({
  organisationId,
  selection,
  routeByKey,
  carrierByKey,
  emptyLabel,
}: {
  organisationId: string | null;
  selection: OrganisationRoute[];
  routeByKey: Map<string, CrossingRoute>;
  carrierByKey: Map<string, CrossingCarrier>;
  emptyLabel: string;
}) {
  const { t } = useTranslation('configuration');
  const starMutation = useSetOrganisationRoutePreferred();

  if (selection.length === 0) {
    return <p className="text-xs italic text-slate-400">{emptyLabel}</p>;
  }

  const rows = selection
    .map((row) => {
      const route = routeByKey.get(row.catalogueKey);
      const carrier = route ? carrierByKey.get(route.carrierKey) : undefined;
      return {
        row,
        label: routeLabel(route) ?? row.catalogueKey,
        carrierCode: carrier?.code ?? null,
        carrierName: carrier?.name ?? null,
      };
    })
    .sort(
      (a, b) =>
        Number(b.row.isPreferred) - Number(a.row.isPreferred) ||
        a.label.localeCompare(b.label) ||
        (a.carrierCode ?? '').localeCompare(b.carrierCode ?? ''),
    );

  const toggle = async (row: OrganisationRoute) => {
    if (!organisationId) return;
    try {
      await starMutation.mutateAsync({
        organisationId,
        organisationRouteId: row.id,
        isPreferred: !row.isPreferred,
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('carriersAndRoutes.starFailed'),
      });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {rows.map(({ row, label, carrierCode, carrierName }) => {
          const starred = row.isPreferred;
          const busy = starMutation.isPending && starMutation.variables?.organisationRouteId === row.id;
          return (
            <span
              key={row.id}
              className={`inline-flex items-center gap-1 rounded-full border py-0.5 pl-1 pr-2.5 text-xs font-medium ${
                starred
                  ? 'border-emerald-600 bg-emerald-600 text-white'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              }`}
              title={carrierName ? `${label} · ${carrierName}` : row.catalogueKey}
            >
              <button
                type="button"
                aria-pressed={starred}
                aria-label={starred ? t('carriersAndRoutes.unstar') : t('carriersAndRoutes.star')}
                title={starred ? t('carriersAndRoutes.unstar') : t('carriersAndRoutes.star')}
                disabled={!organisationId || busy}
                onClick={() => void toggle(row)}
                className={`flex h-4 w-4 items-center justify-center rounded-full disabled:opacity-50 ${
                  starred ? 'text-amber-300 hover:text-amber-200' : 'text-emerald-300 hover:text-amber-500'
                }`}
              >
                <Star className="h-3 w-3" fill={starred ? 'currentColor' : 'none'} />
              </button>
              {label}
              {carrierCode && (
                <span
                  className={`ml-0.5 rounded-sm px-1 text-[10px] font-semibold ${
                    starred ? 'bg-emerald-700 text-emerald-50' : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {carrierCode}
                </span>
              )}
            </span>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400">{t('carriersAndRoutes.starHint')}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Header — how unbooked crossings are suggested
// ────────────────────────────────────────────────────────────────────────────

const PREFERENCES: CrossingPreferenceValue[] = ['Fastest', 'Balanced', 'LessDriving', 'Cheapest'];

/**
 * "Suggest crossings by" — the organisation's crossing preference, saved on
 * click. Cheapest is shown but greyed out: it needs route rates the planner
 * does not have yet, and the server refuses it until then.
 */
function CrossingPreferenceControl({ organisationId }: { organisationId: string | null }) {
  const { t } = useTranslation('configuration');
  const organisationQuery = useOrganisation(organisationId);
  const saveMutation = useSetOrganisationCrossingPreference();
  const current = organisationQuery.data?.crossingPreference ?? 'Fastest';

  const choose = async (next: CrossingPreferenceValue) => {
    if (!organisationId || next === current || next === 'Cheapest') return;
    try {
      await saveMutation.mutateAsync({ organisationId, crossingPreference: next });
      showToast({ type: 'success', message: t('carriersAndRoutes.preferenceSaved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('carriersAndRoutes.preferenceFailed'),
      });
    }
  };

  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {t('carriersAndRoutes.suggestBy')}
      </span>
      <span role="radiogroup" aria-label={t('carriersAndRoutes.suggestBy')} className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
        {PREFERENCES.map((value) => {
          const active = value === current;
          const unavailable = value === 'Cheapest';
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-disabled={unavailable || undefined}
              title={t(`carriersAndRoutes.preferenceHint.${value}`)}
              disabled={!organisationQuery.isSuccess || saveMutation.isPending || unavailable}
              onClick={() => void choose(value)}
              className={`rounded-sm px-2.5 py-0.5 text-xs font-medium transition-colors ${
                active
                  ? 'bg-white text-blue-700 shadow-sm'
                  : unavailable
                    ? 'cursor-not-allowed text-slate-300'
                    : 'text-slate-600 hover:text-slate-900 disabled:opacity-60'
              }`}
            >
              {t(`carriersAndRoutes.preference.${value}`)}
            </button>
          );
        })}
      </span>
    </span>
  );
}

function ChipList<T extends OrganisationCarrier | OrganisationRoute>({
  selection,
  getKey,
  getLabel,
  emptyLabel,
  tone,
}: ChipListProps<T>) {
  if (selection.length === 0) {
    return <p className="text-xs italic text-slate-400">{emptyLabel}</p>;
  }
  const palette =
    tone === 'sky'
      ? 'border-sky-200 bg-sky-50 text-sky-700'
      : 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return (
    <div className="flex flex-wrap gap-1.5">
      {selection.map((row) => (
        <span
          key={row.id}
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${palette}`}
          title={getKey(row)}
        >
          {getLabel(row)}
        </span>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Edit-mode bodies
// ────────────────────────────────────────────────────────────────────────────

interface CarriersEditBodyProps {
  /** My.Ferry's active carriers. */
  carriers: CrossingCarrier[];
  legacy: LegacyPick[];
  draft: Set<string>;
  isBusy: boolean;
  onToggleDraft: (key: string) => void;
}

function CarriersEditBody({
  carriers,
  legacy,
  draft,
  isBusy,
  onToggleDraft,
}: CarriersEditBodyProps) {
  const { t } = useTranslation('configuration');
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">{t('carriersAndRoutes.selectCarriersPrompt')}</p>
      {carriers.length === 0 ? (
        <Unavailable label={t('carriersAndRoutes.catalogueUnavailable')} />
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {carriers.map((carrier) => (
            <ToggleChip
              key={carrier.key}
              active={draft.has(carrier.key)}
              disabled={isBusy || !carrier.linked}
              title={carrier.linked ? undefined : t('carriersAndRoutes.notLinked')}
              onToggle={() => onToggleDraft(carrier.key)}
              tone="sky"
            >
              {carrier.name}
              {!carrier.linked && <NotLinkedMark />}
            </ToggleChip>
          ))}
        </div>
      )}
      <LegacyPicks picks={legacy} draft={draft} isBusy={isBusy} onToggleDraft={onToggleDraft} tone="sky" />
    </div>
  );
}

/** Ferry's sea areas, in the order the chips show them. */
const CATEGORY_ORDER: FerryRouteCategory[] = [
  'IrishSea',
  'CelticSea',
  'EnglishChannel',
  'NorthSea',
  'BayOfBiscay',
  'BalticSea',
  'Other',
  'Unknown',
];

interface RoutesEditBodyProps {
  groups: Array<{
    carrierKey: string;
    carrier: CrossingCarrier | null;
    routes: CrossingRoute[];
  }>;
  legacy: LegacyPick[];
  draft: Set<string>;
  isBusy: boolean;
  onToggleDraft: (key: string) => void;
}

function RoutesEditBody({
  groups,
  legacy,
  draft,
  isBusy,
  onToggleDraft,
}: RoutesEditBodyProps) {
  const { t } = useTranslation('configuration');
  const [category, setCategory] = useState<FerryRouteCategory | 'all'>('all');

  const categoryOf = (route: CrossingRoute): FerryRouteCategory => route.category ?? 'Unknown';
  const categories = CATEGORY_ORDER.filter((c) =>
    groups.some((g) => g.routes.some((r) => categoryOf(r) === c)),
  );
  const shown = groups
    .map((g) => ({
      ...g,
      routes: category === 'all' ? g.routes : g.routes.filter((r) => categoryOf(r) === category),
    }))
    .filter((g) => g.routes.length > 0);

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">{t('carriersAndRoutes.selectRoutesPrompt')}</p>
      {groups.length === 0 ? (
        <Unavailable label={t('carriersAndRoutes.catalogueUnavailable')} />
      ) : (
        <>
          {categories.length > 1 && (
            <span
              role="radiogroup"
              aria-label={t('carriersAndRoutes.seaArea')}
              className="inline-flex flex-wrap rounded-md border border-slate-200 bg-slate-50 p-0.5"
            >
              <CategoryChip active={category === 'all'} onClick={() => setCategory('all')}>
                {t('carriersAndRoutes.allCategories')}
              </CategoryChip>
              {categories.map((c) => (
                <CategoryChip key={c} active={category === c} onClick={() => setCategory(c)}>
                  {t(`carriersAndRoutes.category.${c}`)}
                </CategoryChip>
              ))}
            </span>
          )}
          {shown.map((group) => (
            <div key={group.carrierKey}>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {group.carrier?.name ?? t('carriersAndRoutes.unknownCarrier')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {group.routes.map((route) => (
                  <ToggleChip
                    key={route.key}
                    active={draft.has(route.key)}
                    disabled={isBusy || !route.linked}
                    title={route.linked ? undefined : t('carriersAndRoutes.notLinked')}
                    onToggle={() => onToggleDraft(route.key)}
                    tone="emerald"
                  >
                    {routeLabel(route) ?? route.key}
                    {route.accompaniment === 'unaccompanied' && (
                      <span className="ml-1 text-[10px] font-normal opacity-75">
                        · {t('carriersAndRoutes.trailersOnly')}
                      </span>
                    )}
                    {!route.linked && <NotLinkedMark />}
                  </ToggleChip>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
      <LegacyPicks
        picks={legacy}
        draft={draft}
        isBusy={isBusy}
        onToggleDraft={onToggleDraft}
        tone="emerald"
      />
    </div>
  );
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`rounded-sm px-2 py-0.5 text-[11px] font-medium transition-colors ${
        active ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
      }`}
    >
      {children}
    </button>
  );
}

interface LegacyPick {
  key: string;
  label: string;
}

/**
 * Picks from before the move to Ferry, still saved under a catalogue-file
 * key. Offered only for removal: untick and Save drops the row.
 */
function LegacyPicks({
  picks,
  draft,
  isBusy,
  onToggleDraft,
  tone,
}: {
  picks: LegacyPick[];
  draft: Set<string>;
  isBusy: boolean;
  onToggleDraft: (key: string) => void;
  tone: 'sky' | 'emerald';
}) {
  const { t } = useTranslation('configuration');
  if (picks.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
        {t('carriersAndRoutes.legacyGroup')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {picks.map((pick) => (
          <ToggleChip
            key={pick.key}
            active={draft.has(pick.key)}
            disabled={isBusy}
            onToggle={() => onToggleDraft(pick.key)}
            tone={tone}
          >
            {pick.label}
          </ToggleChip>
        ))}
      </div>
    </div>
  );
}

/** A Ferry entry the catalogue file does not link yet: visible, not pickable. */
function NotLinkedMark() {
  const { t } = useTranslation('configuration');
  return (
    <span className="ml-1 text-[10px] font-normal opacity-75">
      · {t('carriersAndRoutes.notLinkedShort')}
    </span>
  );
}

function Unavailable({ label }: { label: string }) {
  return (
    <p className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">
      {label}
    </p>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Toggle chip
// ────────────────────────────────────────────────────────────────────────────

interface ToggleChipProps {
  active: boolean;
  disabled: boolean;
  onToggle: () => void;
  tone: 'sky' | 'emerald';
  /** Shown on hover; says why a chip cannot be picked. */
  title?: string;
  children: React.ReactNode;
}

function ToggleChip({ active, disabled, onToggle, tone, title, children }: ToggleChipProps) {
  // Active chip = solid fill in the tone colour; inactive = neutral outline
  // that hover-shades into the tone so the affordance is obvious.
  const activeClasses =
    tone === 'sky'
      ? 'border-sky-600 bg-sky-600 text-white'
      : 'border-emerald-600 bg-emerald-600 text-white';
  const inactiveHover =
    tone === 'sky'
      ? 'hover:border-sky-400 hover:text-sky-700'
      : 'hover:border-emerald-400 hover:text-emerald-700';
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      disabled={disabled}
      title={title}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors disabled:opacity-50 ${
        active ? activeClasses : `border-slate-300 bg-white text-slate-600 ${inactiveHover}`
      }`}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/** The keys at edit start that Ferry does not offer, labelled where they still resolve. */
function legacyPicks(
  startKeys: readonly string[],
  offered: ReadonlyArray<{ key: string }>,
  labelOf: (key: string) => string | null | undefined,
): LegacyPick[] {
  const offeredKeys = new Set(offered.map((o) => o.key));
  return startKeys
    .filter((key) => !offeredKeys.has(key))
    .map((key) => ({ key, label: labelOf(key) ?? key }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Compact label for a route: `"Dublin → Holyhead"` (origin → destination
 * port names). Falls back to `route.name` when the explicit port fields
 * are missing.
 */
function routeLabel(route: CrossingRoute | null | undefined): string | null {
  if (!route) return null;
  if (route.originPortName && route.destinationPortName) {
    return `${route.originPortName} → ${route.destinationPortName}`;
  }
  return route.name;
}
